import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, CheckCircle, Gauge, Globe, Phone, ShieldCheck, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { PRODUCT_CATEGORIES, PRODUCTS } from '../data/products';
import { INDUSTRIES } from '../data/industries';
import { COMPANY_INFO } from '../data/company';
import { SUPPORT_FAQS } from '../data/supportFaqs';
import { ProductCard } from '../components/common/ProductCard';
import { QuoteForm } from '../components/common/QuoteForm';
import ScrollingCustomers from '../components/common/ScrollingCustomers';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const HomePage = () => {
  const featuredProducts = PRODUCTS.slice(0, 4);
  const phoneNumbers = COMPANY_INFO.contact.phone || [];
  const trustCards = [
    {
      icon: ShieldCheck,
      title: COMPANY_INFO.certification,
      text: 'Quality-controlled manufacturing, documentation, and service processes for dependable project delivery.',
    },
    {
      icon: Gauge,
      title: 'Metrology-led accuracy',
      text: 'Load cells, indicators, and calibration workflows selected for industrial accuracy and long-term stability.',
    },
    {
      icon: Wrench,
      title: 'Site-ready support',
      text: 'Engineering assistance across foundation planning, installation, commissioning, and after-sales service.',
    },
  ];
  const statCards = [
    { value: COMPANY_INFO.stats.yearsExperience, label: 'Years Experience' },
    { value: COMPANY_INFO.stats.installations, label: 'Installations' },
    { value: COMPANY_INFO.stats.countries, label: 'Countries Served' },
    { value: COMPANY_INFO.stats.customers, label: 'Happy Customers' },
  ];

  return (
    <div data-testid="home-page">
      <Seo {...routeMeta['/']} path="/" />
      <section className="home-hero-surface tech-grid relative flex min-h-[90vh] flex-col justify-center overflow-hidden pb-0 lg:pb-28">
        <div className="absolute inset-0 bg-[linear-gradient(100deg,rgba(7,19,40,0.95)_0%,rgba(9,27,56,0.86)_48%,rgba(6,16,31,0.9)_100%)]" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: 'easeOut' }}>
              <motion.div
                initial={false}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.35 }}
                className="section-eyebrow mb-8"
              >
                Strategic Weighing Systems
              </motion.div>

              <motion.h1
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.16, duration: 0.48, ease: 'easeOut' }}
                className="font-['Manrope'] max-w-4xl text-4xl font-extrabold leading-[0.96] tracking-tight text-white uppercase sm:text-5xl md:text-7xl md:leading-[0.92]"
              >
                Industrial
                <br />
                weighing
                <br />
                solutions for
                <br />
                <span className="text-[#93C5FD]">modern industry.</span>
              </motion.h1>

              <motion.p
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.24, duration: 0.45, ease: 'easeOut' }}
                className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-300 md:text-xl"
              >
                Precision-engineered weighbridges, truck scales, indicators, and platform scales for industrial sites in Ghana, India, Africa, Dubai, and global markets.
              </motion.p>

              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32, duration: 0.42, ease: 'easeOut' }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <Link to="/products">
                  <Button className="tech-shimmer rounded-sm bg-[#1E40AF] px-8 py-6 text-sm font-bold uppercase tracking-[0.18em] text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2563EB]" data-testid="hero-explore-btn">
                    Explore Products
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact#quote-form">
                  <Button variant="outline" className="ghost-button rounded-sm px-8 py-6 text-sm font-bold uppercase tracking-[0.18em] transition-all duration-200 hover:-translate-y-0.5" data-testid="hero-quote-btn">
                    Get a Quote
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38, duration: 0.42 }}
                className="mt-12 flex flex-wrap items-center gap-4 text-sm text-slate-300"
              >
                <div className="inline-flex items-center gap-2 border border-white/15 bg-[#0B1F3B]/70 px-4 py-3 text-white backdrop-blur-sm">
                  <Award className="h-4 w-4 text-[#60A5FA]" />
                  {COMPANY_INFO.certification}
                </div>
                <div className="inline-flex items-center gap-2 border border-white/15 bg-[#0B1F3B]/70 px-4 py-3 text-white backdrop-blur-sm">
                  <span className="font-['JetBrains_Mono'] text-[#93C5FD]">{COMPANY_INFO.stats.installations}</span>
                  deployed installations
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, delay: 0.16, ease: 'easeOut' }}
              className="block w-full lg:-mr-10 lg:pb-6 xl:-mr-20"
            >
              <motion.div
                transition={{ duration: 0.24, ease: 'easeOut' }}
                className="tech-panel tech-glow depth-card relative overflow-hidden rounded-sm"
              >
                <div className="absolute inset-x-4 top-4 z-10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.22em] text-white/70 sm:inset-x-6 sm:top-5 sm:text-[11px] sm:tracking-[0.35em]">
                  <span>Live Installation</span>
                  <span>Field View</span>
                </div>
                <div className="relative h-[240px] overflow-hidden sm:h-[320px] lg:h-[380px] xl:h-[430px]">
                  <img
                    src="/assets/hero-whatsapp-weighbridge.jpeg"
                    alt="Pitless Weighbridge Installation"
                    loading="eager"
                    className="media-grade h-full w-full object-cover object-center"
                  />
                </div>
                <div className="absolute bottom-4 left-4 right-4 max-w-[calc(100%-2rem)] rounded-sm border border-white/15 bg-[#0B1F3B]/78 px-4 py-3 text-white backdrop-blur-md sm:bottom-5 sm:left-5 sm:right-auto sm:max-w-none">
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-300 sm:tracking-[0.35em]">Weighbridge Node</div>
                  <div className="mt-1 text-base font-bold sm:text-lg">Operational Surface Deck</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 w-full border-t border-white/10 bg-[#06101f] lg:absolute lg:bottom-0 lg:left-0 lg:right-0">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-8">
              {statCards.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.46 + i * 0.06, duration: 0.36 }}
                  className="metric-card rounded-sm border border-[#60A5FA]/20 bg-[#0E2344] px-3 py-4 text-center shadow-[0_12px_28px_rgba(2,6,23,0.3)]"
                >
                  <div className="font-['JetBrains_Mono'] text-3xl font-extrabold text-[#93C5FD] md:text-4xl">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-sm uppercase tracking-wider text-white">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ScrollingCustomers />

      <section className="tech-grid bg-[#06101f] py-16 md:py-24" data-testid="trust-authority-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.35 }}>
              <span className="section-eyebrow">Engineering Confidence</span>
              <h2 className="font-['Manrope'] mt-4 text-3xl font-bold uppercase tracking-tight text-white md:text-5xl">
                Built for sites where downtime costs money.
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                LEION presents weighing equipment as a complete project system for Ghana, India, and international markets: platform, electronics, calibration, software, service, and site guidance working together.
              </p>
            </motion.div>

            <div className="grid gap-4 md:grid-cols-3">
              {trustCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.06, duration: 0.32 }}
                  className="premium-trust-card border border-white/10 bg-white/5 p-6"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center border border-[#60A5FA]/30 bg-[#1E40AF]/20 text-[#93C5FD]">
                    <card.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{card.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-300">{card.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="tech-grid bg-[#08152b] py-16 md:py-24" data-testid="field-proof-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.34 }}
            className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end"
          >
            <div>
              <span className="section-eyebrow">Field Proof</span>
              <h2 className="font-['Manrope'] mt-4 text-3xl font-bold uppercase tracking-tight text-white md:text-5xl">
                Real weighing systems, presented with confidence.
              </h2>
            </div>
            <p className="max-w-2xl text-base leading-8 text-slate-300">
              Product photography helps customers see scale, finish, deployment context, and reliability before they request a quote for a weighbridge or weighing scale system.
            </p>
          </motion.div>

          <div className="grid gap-5 md:grid-cols-3">
            {[
              { src: '/assets/product-logo-pitless-weighbridge.png', label: 'Surface Deck', title: 'Pitless installation' },
              { src: '/assets/product-logo-pit-type-weighbridge.png', label: 'Heavy Duty', title: 'Truck scale systems' },
              { src: '/assets/hitech-combi-indicator.jpg', label: 'Control Node', title: 'Digital indicators' },
            ].map((item, index) => (
              <motion.div
                key={item.src}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06, duration: 0.32 }}
                className="premium-media-frame group aspect-[4/3]"
              >
                <img src={item.src} alt={item.title} loading="lazy" className="media-grade h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute bottom-4 left-4 right-4 z-10 border border-white/15 bg-[#06101f]/72 p-4 text-white backdrop-blur-md">
                  <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#93C5FD]">{item.label}</div>
                  <div className="mt-1 text-lg font-bold">{item.title}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="tech-grid bg-[#08152b] py-20 md:py-32" data-testid="product-categories-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end"
          >
            <div>
              <span className="section-eyebrow">Our Product Range</span>
              <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
                Systems built around the weighing platform.
              </h2>
            </div>
            <p className="max-w-3xl text-lg leading-relaxed text-slate-300 lg:ml-auto">
              From heavy-duty weighbridges to software dashboards and precision scales, every product supports industrial weighing requirements across Ghana, India, Africa, Dubai, and international markets.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PRODUCT_CATEGORIES.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06, duration: 0.3 }}
              >
                <Link
                  to={`/products/${category.id}`}
                  className="group depth-card premium-product-card block h-full overflow-hidden border border-white/10 bg-white/5 transition-all duration-300 hover:border-[#60A5FA] hover:shadow-[0_22px_50px_rgba(30,64,175,0.18)]"
                  data-testid={`category-${category.id}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-[radial-gradient(circle_at_top,rgba(96,165,250,0.14),transparent_42%),linear-gradient(180deg,#112448_0%,#0B1730_100%)]">
                    <img
                      src={category.image}
                      alt={category.name}
                      loading="lazy"
                      className={`media-grade h-full w-full transition-transform duration-500 group-hover:scale-105 ${category.imageFit === 'contain' ? 'object-contain p-5' : 'object-cover'}`}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white transition-colors group-hover:text-[#60A5FA]">
                      {category.name}
                    </h3>
                    <p className="mt-2 text-sm text-slate-300">
                      {category.description}
                    </p>
                    <div className="mt-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[#60A5FA]">
                      <span>View Range</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="tech-grid bg-[#071327] py-20 md:py-32" data-testid="featured-products-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <span className="section-eyebrow">Featured Systems</span>
              <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
                Core LEION equipment
              </h2>
            </div>
            <Link to="/products">
              <Button variant="outline" className="rounded-sm border-2 border-[#60A5FA] px-6 py-4 font-bold uppercase tracking-wider text-[#60A5FA] hover:bg-[#60A5FA] hover:text-[#08152b]" data-testid="view-all-products-btn">
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {featuredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B1F3B] py-20 md:py-32" data-testid="industries-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <span className="section-eyebrow">Industries We Serve</span>
            <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
              Trusted Across Sectors
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {INDUSTRIES.map((industry, index) => (
              <motion.div key={industry.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }}>
                <Link
                  to="/industries"
                  className="group block min-h-[150px] border border-white/10 bg-white/5 p-6 text-center transition-all duration-200 hover:-translate-y-1 hover:border-[#60A5FA] hover:bg-white/10 hover:shadow-[0_18px_50px_rgba(96,165,250,0.16)]"
                  data-testid={`industry-${industry.id}`}
                >
                  <div className="mb-3 text-[#60A5FA]">
                    <Globe className="mx-auto h-8 w-8" />
                  </div>
                  <h3 className="text-sm font-semibold text-white transition-colors group-hover:text-[#60A5FA]">
                    {industry.name}
                  </h3>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/industries">
              <Button className="rounded-sm border-2 border-[#60A5FA] bg-transparent px-8 py-4 font-bold uppercase tracking-wider text-[#60A5FA] hover:bg-[#60A5FA] hover:text-[#08152b]" data-testid="view-industries-btn">
                Explore Industries
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="tech-grid bg-[#071327] py-20 md:py-32" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="section-eyebrow">Why Choose LEION</span>
              <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
                Engineering Excellence
              </h2>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
                For over {COMPANY_INFO.stats.yearsExperience} years, LEION Engineering has built industrial weighing technology for businesses in India, Ghana, Africa, Dubai, and global markets. Our commitment to precision and reliability makes us a trusted choice for demanding sites.
              </p>

              <div className="mt-10 space-y-6">
                {COMPANY_INFO.values.map((value, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-[#1E40AF]">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{value.title}</h4>
                      <p className="mt-1 text-sm text-slate-300">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/about">
                  <Button className="tech-shimmer rounded-sm bg-[#1E40AF] px-8 py-4 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] hover:bg-[#2563EB]" data-testid="learn-more-btn">
                    Learn More About Us
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="absolute -inset-4 bg-[#1E40AF] opacity-10 blur-2xl" />
              <img
                src="/assets/product-logo-pit-type-weighbridge.png"
                alt="Pit Type Weighbridge Installation"
                loading="lazy"
                className="media-grade relative w-full rounded-sm border border-white/10 object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1E40AF] p-6 text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)]">
                <div className="font-['JetBrains_Mono'] text-4xl font-extrabold">{COMPANY_INFO.stats.installations}</div>
                <div className="mt-1 text-sm font-bold uppercase tracking-wider">Installations</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="tech-grid bg-[#071327] py-20 md:py-28" data-testid="home-faq-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <span className="section-eyebrow">Weighbridge Guidance</span>
            <h2 className="font-['Manrope'] mt-4 text-3xl font-bold uppercase tracking-tight text-white md:text-5xl">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {SUPPORT_FAQS.map((faq, index) => (
              <AccordionItem
                key={faq.question}
                value={`home-faq-${index}`}
                className="border border-white/15 bg-white/5 px-6"
                data-testid={`home-faq-${index}`}
              >
                <AccordionTrigger className="cursor-pointer py-6 text-left font-semibold text-white transition-colors duration-200 hover:text-[#93C5FD] [&>svg]:text-[#93C5FD]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent forceMount className="pb-6 leading-7 text-slate-200">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="tech-grid bg-[#08152b] py-20 md:py-32" data-testid="cta-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <span className="section-eyebrow">Get Started</span>
              <h2 className="font-['Manrope'] mt-4 text-4xl font-bold uppercase tracking-tight text-white md:text-5xl">
                Request a Quote
              </h2>
              <p className="mt-6 text-lg text-slate-300">
                Tell us about your weighing requirements and our engineering team will provide a customized solution within 24 hours.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <Phone className="h-6 w-6 text-[#1E40AF]" />
                <div>
                  <div className="text-sm text-slate-400">Call us directly</div>
                  <div className="flex flex-col gap-1">
                    {phoneNumbers.map((phone) => (
                      <a key={phone} href={`tel:${phone}`} className="text-xl font-bold text-white hover:text-[#60A5FA]">
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="tech-panel depth-card border border-white/10 bg-white/95 p-8 shadow-[0_20px_50px_rgba(30,64,175,0.18)]"
            >
              <QuoteForm compact />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
